import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { authAPI } from '../../utils/api';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import brandLogo from '../../assets/brand/logo.png';

const ForgotPasswordPage = () => {
  const { t } = usePublicLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!email) {
      setError(t('auth.validation.emailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('auth.validation.emailInvalid'));
      return;
    }

    setLoading(true);

    try {
      await authAPI.forgotPassword({ email });
      setEmailSent(true);
      toast.success(t('auth.forgotPassword.emailSentSuccess'));
    } catch (error) {
      if (error.response?.data?.error) {
        // Don't reveal if email exists or not for security
        setError(t('auth.forgotPassword.checkEmailMessage'));
      } else {
        setError(t('auth.error.internal'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl"
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4"
            >
              <CheckCircle className="h-8 w-8 text-green-600" />
            </motion.div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.forgotPassword.emailSentTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('auth.forgotPassword.emailSentDescription').replace('{{email}}', email)}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('auth.forgotPassword.checkSpam')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center text-primary-600 hover:text-primary-500 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl"
      >
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-block mb-6">
            <img
              src={brandLogo}
              alt="Candio logo"
              className="h-14 w-14 object-contain mx-auto"
            />
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('auth.forgotPassword.title')}
          </h2>
          <p className="text-gray-600">
            {t('auth.forgotPassword.subtitle')}
          </p>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
          noValidate
        >
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.forgotPassword.emailLabel')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!error}
                aria-describedby={error ? 'forgot-password-email-error' : undefined}
                className={`input pl-10 ${error ? 'border-red-500 focus:border-red-500' : ''}`}
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
              />
            </div>
            {error && (
              <p id="forgot-password-email-error" className="mt-1 text-sm text-red-600">{error}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary btn-lg relative"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('common.loading')}
              </>
            ) : (
              t('auth.forgotPassword.sendResetLink')
            )}
          </button>

          {/* Back to Login */}
          <div className="text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('auth.forgotPassword.backToLogin')}
            </Link>
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
